import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Modal, Col } from "react-bootstrap";
import { isEmpty } from "lodash";
import { userConstants } from "actions/types";
import { scrollToTop } from "utils/dom";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { getUserState, updateSelectedAccountType } from "actions/userActions";
import { getIraAccountIdSelector } from "store/selectors/user";
import { INVESTMENT_PROFILE } from "statics/onboardingSteps";
import { accountTypesToEnglishMapping } from "statics/accountTypes";
import { FiAlertTriangle } from "react-icons/fi";
import type { RootState } from "types/state";
import type { AccountType } from "types/graphql";

import ProgressBar from "components/ProgressBar";
import SelectBox from "./SelectBox";
import Button from "components/Button";
import Alert from "components/Alert";
import CircularProgressBar from "components/CircularProgressBar";
import DisclaimerBox from "components/DisclaimerBox";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import IconCircle from "components/IconCircle";

import "./Individual401kConfirmRolloverAccountType.scss";

interface RolloverAccountType {
  label: string;
  key: string;
  description: string;
  // read in the SelectBox JSX below but never set here — resolves to undefined
  items?: React.ReactNode[];
  ordered?: boolean;
}

const rolloverAccountTypes: RolloverAccountType[] = [
  {
    label: "Traditional",
    key: "TRADITIONAL_IRA",
    description:
      "A Traditional IRA is pre-tax. Withdrawals are taxed as income.",
  },
  {
    label: "Roth ",
    key: "ROTH_IRA",
    description: "A Roth IRA is post-tax. Qualified withdrawals are tax-free.",
  },
];

const actions = [userConstants.UPDATE_SELECTED_ACCOUNT_TYPE];

const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  const userState = (state.user.userState as { state: string }).state;
  const accounts = state.accounts.iraAccounts;
  return {
    isSubmitting: loadingSelector(state),
    error: errorSelector(state),
    userState,
    accountId: getIraAccountIdSelector(state),
    accounts,
  };
};

const mapDispatchToProps = {
  getUserState,
  updateSelectedAccountType,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  onCancel: () => void;
}

type Props = OwnProps & WithApolloClient<PropsFromRedux>;

interface State {
  selectedAccountType: string;
  submitted: boolean;
  showPartialRolloverConfirmationModal: boolean;
}

class Individual401kPartialRollover extends React.Component<Props, State> {
  _isMounted = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      selectedAccountType: "",
      submitted: false,
      showPartialRolloverConfirmationModal: false,
    };
  }

  componentDidMount() {
    window.analytics.page("Individual Account Type Selection");
    scrollToTop();
  }

  _submitAccountType = () => {
    const accountType = this.state.selectedAccountType;

    if (accountType === "TRADITIONAL_IRA") {
      window.analytics.track("Partial Traditional IRA Rollover Selected");
    } else if (accountType === "ROTH_IRA") {
      window.analytics.track("Partial Roth IRA Rollover Selected");
    }

    void this.props
      .updateSelectedAccountType(
        this.props.client as ApolloClient<object>,
        // narrow AccountType union — cast at the call boundary.
        accountType as AccountType
      )
      .then(() => {
        void this.props.getUserState(this.props.client as ApolloClient<object>);
      });
    window.analytics.track("Submitted Partial Rollover Account Type");

    this.setState({
      submitted: true,
    });
  };

  _setSelectedAccountType = (selectedAccountType: string) => {
    this.setState({
      selectedAccountType,
    });
  };

  _disclaimerLink = () => {
    return (
      <p>Just a reminder, your 401(k) has both pre-tax and post-tax savings.</p>
    );
  };

  _partialRolloverConfirmationModal = () => {
    const rolloverFundType =
      this.state.selectedAccountType === "TRADITIONAL_IRA"
        ? "Traditional"
        : "Roth";

    const subtext = `Your Paychex 401(k) contains both Traditional and Roth savings. Are you sure you only want to rollover your ${rolloverFundType} savings? `;

    const modalBtnLabel = `Rollover, ${rolloverFundType} savings only`;

    return (
      <Modal
        centered={true}
        show={this.state.showPartialRolloverConfirmationModal}
        onSuccess={() =>
          this.setState({
            showPartialRolloverConfirmationModal: false,
          })
        }
      >
        <Modal.Body
          style={{
            padding: 20,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <IconCircle
            type="primary"
            icon={<FiAlertTriangle color="white" stroke="#60A4BF" size={18} />}
          />
          <IconHeader
            variant="cardHeader"
            headerText="Confirm Partial Rollover"
          />
          <IconSubheader
            subheader={subtext}
            style={{
              textAlign: "center",
            }}
          />
          <div
            className="btn-row"
            style={{
              display: "flex",
              justifyContent: "space-between",
              paddingTop: 20,
            }}
          >
            <Button
              color="dismiss"
              btnLabel="Cancel"
              type="button"
              name="cancel"
              onClick={() => {
                this.setState({
                  showPartialRolloverConfirmationModal: false,
                });
              }}
            />
            <Button
              name="submit"
              withArrow={true}
              btnLabel={modalBtnLabel}
              onClick={this._submitAccountType}
              loading={this.props.isSubmitting}
            />
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  render() {
    const btnLabel = this.state.selectedAccountType
      ? `Select ${
          accountTypesToEnglishMapping[
            this.state
              .selectedAccountType as keyof typeof accountTypesToEnglishMapping
          ]
        } Account`
      : "Select Account";

    return (
      <div id="account-selection">
        <ProgressBar
          isEmployer={false}
          activeStepId={INVESTMENT_PROFILE.id}
          progressPercent={"90"}
        />

        {this.state.showPartialRolloverConfirmationModal && (
          <>{this._partialRolloverConfirmationModal()}</>
        )}

        <div className="mega-container">
          <div className="step-container is-active" data-circle-percent="100">
            <section className="page-title-wrap">
              <article className="text-cell">
                <p className="page-eyebrow">YOUR INVESTMENT PROFILE</p>
                <p className="page-title">Select an account to rollover.</p>
              </article>
              <article className="progress-cell">
                <ul className="circular-progress-wrap">
                  <CircularProgressBar
                    strokeWidth="8"
                    sqSize="75"
                    percentage="90"
                  />
                </ul>
              </article>
            </section>
            <div>
              <Col lg={7}>
                <div className="account-types">
                  {rolloverAccountTypes.map((account) => (
                    <SelectBox
                      label={account.label}
                      description={account.description}
                      key={account.key}
                      items={account.items}
                      ordered={account.ordered}
                      onSelect={() => this._setSelectedAccountType(account.key)}
                      isSelected={
                        this.state.selectedAccountType === account.key
                      }
                    />
                  ))}
                </div>
                <DisclaimerBox
                  disclaimerText={
                    "Just a reminder, your 401(k) has both pre-tax and post-tax savings."
                  }
                />
              </Col>

              <section className="form-sec-2col">
                <article className="col-form">
                  <div className="submit-row">
                    {this.props.error && (
                      <Alert type="error" msg={this.props.error} />
                    )}
                  </div>
                  <div className="btn-row">
                    <Button
                      btnLabel="Go Back"
                      name="cancel"
                      color="cancel"
                      onClick={() => {
                        window.analytics.track(
                          "Partial Rollover Flow Abandoned"
                        );
                        this.props.onCancel();
                      }}
                    />
                    <Button
                      name="submit"
                      btnLabel={btnLabel}
                      withArrow={true}
                      onClick={() => {
                        this.setState({
                          showPartialRolloverConfirmationModal: true,
                        });
                      }}
                      disabled={isEmpty(this.state.selectedAccountType)}
                    />
                  </div>
                </article>
              </section>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(Individual401kPartialRollover));
